import {
  ButtonType,
  ButtonWidget,
  CompactInlineAlertWidget,
  InlineAlertProps,
  InlineAlertType,
} from '@tableau/widgets';
import React from 'react';

import { RemoteType } from '../../../../../gen-src/queries';
import { TableauUser } from '../UserDisplayComponent';
import { DataQualityDetailAuthorElement, DataQualityDetailDateElement, DataQualityDetailAsset, DataQualityIndicatorAssetType, DataQualityDetailMessageElement, DataQualityDetailChipId, DataQualityWarningKind } from '../dataQualityIndicators/DataQualityIndicatorSharedHelpers';
import { WarningIcon } from '../labels/warning/WarningButton';
import {
  DataQualityDetailStyle,
  DataQualityStatusIconButtonStyle,
} from '../style/DataQualityStyles';
import { assertUnreachable } from '../utils/TypescriptHelpers';
import { DataQualityType, localizeDataQualityTypeName } from './DataQualityType';

/**
 * @deprecated TFS-1478812
*/
export interface DataQualityDetailProps {
  readonly onContentItem: boolean;
  readonly assetName: string | null;
  readonly assetType: DataQualityIndicatorAssetType | null;
  readonly assetUrl?: string;
  readonly author: TableauUser;
  readonly newTab?: boolean;
  readonly localizedDate: string;
  readonly type: DataQualityType;
  readonly message: string | null;
  readonly linksDisabled?: boolean;
  readonly isSevere: boolean;
  readonly remoteType?: RemoteType;
}

/**
 * @deprecated TFS-1478812
*/
export type DataQualityTooltipButtonBaseProps = {
  readonly text: string;
  readonly testId: string;
  readonly warningKind: DataQualityWarningKind;
};

/**
 * @deprecated TFS-1478812
*/
export type DataQualityTooltipButtonProps = ({
  readonly kind: 'interactive-icon';
  readonly setRef: (instance: ButtonWidget) => void;
  readonly onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  readonly tabIndex?: number;
} & DataQualityTooltipButtonBaseProps) | ({
  readonly kind: 'interactive-inline';
  readonly onClick: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
} & DataQualityTooltipButtonBaseProps) | ({
  readonly kind: 'non-interactive-inline';
} & DataQualityTooltipButtonBaseProps);

/**
 * SCARY NOTE: if this is an "interactive-inline" the ref comes from React.forwardRef()
 * if it's "interactive-icon" the setRef callback is used. When the SharedWidgets team allows forward ref in ButtonWidget, get rid of setRef
 */
/**
 * @deprecated TFS-1478812
*/
export const DataQualityTooltipButton = React.forwardRef(function DataQualityTooltipButton(props: DataQualityTooltipButtonProps, ref: React.RefObject<HTMLDivElement>) {
  const inlineAlertProps: InlineAlertProps & React.HTMLAttributes<HTMLDivElement> = {
    testId: props.testId,
    type: props.warningKind === 'severe-warning' ? InlineAlertType.Warning : InlineAlertType.Info,
    children: props.text ?? props.text
  };

  switch (props.kind) {
    case 'interactive-icon':
      return (
        <ButtonWidget
          style={DataQualityStatusIconButtonStyle}
          title={''}
          ref={props.setRef}
          testId={props.testId}
          onClick={props.onClick}
          buttonType={ButtonType.IconOutline}
          getContent={s => <WarningIcon isSevere={() => props.warningKind === 'severe-warning'} hover={s.hover || false}/>}
          tabIndex={props.tabIndex}
        />
      );
    case 'interactive-inline':
      return (
        <CompactInlineAlertWidget
          {...inlineAlertProps}
          ref={ref}
          onClick={props.onClick}
        />
      );
    case 'non-interactive-inline':
      return (
        <CompactInlineAlertWidget {...inlineAlertProps} />
      );
    default:
      assertUnreachable(props);
      return <></>;
  }
});

/**
 * @deprecated TFS-1478812
*/
export function DataQualityDetail(props: DataQualityDetailProps): JSX.Element {
  const inlineProps: DataQualityTooltipButtonProps = {
    warningKind: props.isSevere ? 'severe-warning' : 'standard-warning',
    testId: DataQualityDetailChipId,
    kind: 'non-interactive-inline',
    text: localizeDataQualityTypeName(props.type),
  };

  return (
    <DataQualityDetailStyle>
      <DataQualityTooltipButton {...inlineProps} />
      <DataQualityDetailAsset
        assetName={props.assetName}
        assetType={props.assetType}
        assetUrl={props.linksDisabled ? null : props.assetUrl || null}
        remoteType={props.remoteType}
      />
      <DataQualityDetailAuthorElement linksDisabled={props.linksDisabled ?? false} kind={props.isSevere ? 'severe-warning' : 'standard-warning'} {...props} />
      <DataQualityDetailDateElement
        localizedDate={props.localizedDate}
      />
      <DataQualityDetailMessageElement {...props} />
    </DataQualityDetailStyle>
  );
}
